import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { adminFetch } from "utils/adminFetch";

const TYPE_OPTIONS = [
  { value: "TimeLimited", label: "TimeLimited" },
  { value: "SlotLimited", label: "SlotLimited" },
];

const PATH_OPTIONS = [
  { value: "BP", label: "BP" },
  { value: "PB", label: "PB" },
];

function inferPathFromPromoCode(code) {
  const c = String(code || "")
    .trim()
    .toUpperCase();
  if (!c) return "BP";
  const hasBp = c.includes("BP");
  const hasPb = c.includes("PB");
  const hasPp = c.includes("PP");
  if ((hasPb || hasPp) && !hasBp) return "PB";
  if (hasBp && !hasPb && !hasPp) return "BP";
  return "BP";
}

function toYmd(v) {
  if (!v) return "";
  const s = String(v);
  if (s.length >= 10 && s[4] === "-") return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

/** Formats discount % (API camelCase or Supabase snake_case fields). */
function formatDiscountPct(r) {
  const raw =
    r.discountPercent ??
    r.discount_percent ??
    r.percent ??
    r.percentage;
  if (raw === null || raw === undefined || raw === "") return "0";
  const n = Number(raw);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(n));
}

export default function PromoCodeAdmin() {
  const [promoCode, setPromoCode] = useState("");
  const [path, setPath] = useState("BP");
  const [type, setType] = useState("SlotLimited");
  const [timeLimitMode, setTimeLimitMode] = useState("Limited");
  const [number, setNumber] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [discountPercent, setDiscountPercent] = useState("0");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isTimeLimited = type === "TimeLimited";
  const isTimeUnlimited =
    isTimeLimited &&
    String(timeLimitMode || "")
      .trim()
      .toLowerCase() === "unlimited";
  const shouldAskNumber = type === "SlotLimited" || !isTimeUnlimited;

  const loadRows = useCallback(async () => {
    setTableLoading(true);
    setError("");
    try {
      const res = await adminFetch("/api/admin/promo-codes");
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Could not load promo codes.");
        setRows([]);
        return;
      }
      setRows(data.rows || []);
    } catch {
      setError("Network error.");
      setRows([]);
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  function openEdit(r) {
    setEditingRow(r);
    setEditForm({
      promoCode: r.promoCode,
      path: r.path || inferPathFromPromoCode(r.promoCode),
      type: r.type,
      timeLimitMode: r.timeLimitMode === "Unlimited" ? "Unlimited" : "Limited",
      number:
        r.type === "TimeLimited" && r.timeLimitMode === "Unlimited"
          ? ""
          : String(r.total),
      periodStart: toYmd(r.periodStart),
      periodEnd: toYmd(r.periodEnd),
      discountPercent: String(
        r.discountPercent ??
          r.discount_percent ??
          r.percent ??
          r.percentage ??
          0,
      ),
    });
    setError("");
    setMsg("");
  }

  function closeEdit() {
    setEditingRow(null);
    setEditForm(null);
  }

  const handleAdd = async (e) => {
    e.preventDefault();
    setMsg("");
    setError("");
    const n = parseInt(String(number).trim(), 10);
    if (!promoCode.trim()) {
      setError("Promo code is required.");
      return;
    }
    if (!path) {
      setError("Path is required (BP or PB).");
      return;
    }
    if (shouldAskNumber && (!Number.isFinite(n) || n < 1)) {
      setError("Number must be an integer ≥ 1.");
      return;
    }
    if (type === "TimeLimited" && (!periodStart || !periodEnd)) {
      setError("Start and end dates are required for TimeLimited.");
      return;
    }
    const pct = parseInt(String(discountPercent).trim(), 10);
    if (!Number.isFinite(pct) || pct < 0 || pct > 90) {
      setError("Discount %: integer between 0 and 90.");
      return;
    }

    setLoading(true);
    try {
      const body = {
        promoCode: promoCode.trim(),
        path,
        type,
        timeLimitMode: isTimeLimited ? timeLimitMode : null,
        number: shouldAskNumber ? n : null,
        periodStart: type === "TimeLimited" ? periodStart : null,
        periodEnd: type === "TimeLimited" ? periodEnd : null,
        discountPercent: pct,
      };
      const res = await adminFetch("/api/admin/promo-codes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Create failed.");
        return;
      }
      setMsg("Code added.");
      setPromoCode("");
      setPath("BP");
      setTimeLimitMode("Limited");
      setNumber("");
      setPeriodStart("");
      setPeriodEnd("");
      setDiscountPercent("0");
      await loadRows();
    } catch {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingRow || !editForm) return;
    setMsg("");
    setError("");

    const editIsTimeLimited = editForm.type === "TimeLimited";
    const editIsTimeUnlimited =
      editIsTimeLimited &&
      String(editForm.timeLimitMode || "")
        .trim()
        .toLowerCase() === "unlimited";
    const editShouldAskNumber = editForm.type === "SlotLimited" || !editIsTimeUnlimited;

    const n = parseInt(String(editForm.number).trim(), 10);
    if (!editForm.promoCode.trim()) {
      setError("Promo code is required.");
      return;
    }
    if (!editForm.path) {
      setError("Path is required (BP or PB).");
      return;
    }
    if (editShouldAskNumber && (!Number.isFinite(n) || n < 1)) {
      setError("Number must be an integer ≥ 1.");
      return;
    }
    if (editForm.type === "TimeLimited" && (!editForm.periodStart || !editForm.periodEnd)) {
      setError("Start and end dates are required for TimeLimited.");
      return;
    }
    const pct = parseInt(String(editForm.discountPercent).trim(), 10);
    if (!Number.isFinite(pct) || pct < 0 || pct > 90) {
      setError("Discount %: integer between 0 and 90.");
      return;
    }

    setSavingEdit(true);
    try {
      const body = {
        promoCode: editForm.promoCode.trim(),
        path: editForm.path,
        type: editForm.type,
        timeLimitMode: editIsTimeLimited ? editForm.timeLimitMode : null,
        number: editShouldAskNumber ? n : null,
        periodStart: editForm.type === "TimeLimited" ? editForm.periodStart : null,
        periodEnd: editForm.type === "TimeLimited" ? editForm.periodEnd : null,
        discountPercent: pct,
      };
      const res = await adminFetch(`/api/admin/promo-codes/${editingRow.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Update failed.");
        return;
      }
      setMsg("Code updated.");
      closeEdit();
      await loadRows();
    } catch {
      setError("Network error.");
    } finally {
      setSavingEdit(false);
    }
  };

  function requestDelete(r) {
    setDeleteTarget(r);
    setError("");
  }

  function cancelDelete() {
    if (deletingId) return;
    setDeleteTarget(null);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    const r = deleteTarget;
    setError("");
    setMsg("");
    setDeletingId(r.id);
    try {
      const res = await adminFetch(`/api/admin/promo-codes/${r.id}`, {
        method: "DELETE",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Delete failed.");
        return;
      }
      setMsg("Code deleted.");
      setDeleteTarget(null);
      if (editingRow?.id === r.id) closeEdit();
      await loadRows();
    } catch {
      setError("Network error.");
    } finally {
      setDeletingId(null);
    }
  }

  const editIsTimeLimited = editForm?.type === "TimeLimited";
  const editIsTimeUnlimited =
    editIsTimeLimited &&
    String(editForm?.timeLimitMode || "")
      .trim()
      .toLowerCase() === "unlimited";
  const editShouldAskNumber = editForm
    ? editForm.type === "SlotLimited" || !editIsTimeUnlimited
    : false;

  return (
    <div className="mt-3 px-2">
      <h4 className="mb-6 text-2xl font-bold text-navy-700 dark:text-white">
        PromoCode
      </h4>

      <div className="mb-8 rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <h5 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
          Add PromoCode
        </h5>
        <form
          onSubmit={handleAdd}
          className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end"
        >
          <label className="flex min-w-[140px] flex-1 flex-col text-sm">
            <span className="mb-1 text-gray-600 dark:text-gray-300">
              PromoCode
            </span>
            <input
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
              placeholder="CODE2026"
            />
          </label>
          <label className="flex min-w-[100px] flex-col text-sm">
            <span className="mb-1 text-gray-600 dark:text-gray-300">Path</span>
            <select
              value={path}
              onChange={(e) => setPath(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
            >
              {PATH_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex min-w-[160px] flex-col text-sm">
            <span className="mb-1 text-gray-600 dark:text-gray-300">Type</span>
            <select
              value={type}
              onChange={(e) => {
                const nextType = e.target.value;
                setType(nextType);
                if (nextType !== "TimeLimited") {
                  setTimeLimitMode("Limited");
                }
              }}
              className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {type === "TimeLimited" ? (
            <label className="flex min-w-[160px] flex-col text-sm">
              <span className="mb-1 text-gray-600 dark:text-gray-300">Limit</span>
              <select
                value={timeLimitMode}
                onChange={(e) => setTimeLimitMode(e.target.value)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
              >
                <option value="Unlimited">Unlimited</option>
                <option value="Limited">Limited</option>
              </select>
            </label>
          ) : null}
          {shouldAskNumber ? (
            <label className="flex min-w-[100px] flex-col text-sm">
              <span className="mb-1 text-gray-600 dark:text-gray-300">Number</span>
              <input
                type="number"
                min={1}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
                placeholder="100"
              />
            </label>
          ) : null}
          <label className="flex min-w-[100px] flex-col text-sm">
            <span className="mb-1 text-gray-600 dark:text-gray-300">Discount %</span>
            <input
              type="number"
              min={0}
              max={90}
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
              placeholder="0"
            />
          </label>
          {type === "TimeLimited" ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <label className="flex flex-col text-sm">
                <span className="mb-1 text-gray-600 dark:text-gray-300">
                  Period (start)
                </span>
                <input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
                />
              </label>
              <label className="flex flex-col text-sm">
                <span className="mb-1 text-gray-600 dark:text-gray-300">
                  Period (end)
                </span>
                <input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
                />
              </label>
            </div>
          ) : null}
          <button
            type="submit"
            disabled={loading}
            className="rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {loading ? "…" : "Add"}
          </button>
        </form>
        {error ? (
          <p className="mt-3 text-sm text-red-500">{error}</p>
        ) : null}
        {msg ? (
          <p className="mt-3 text-sm text-green-600 dark:text-green-400">{msg}</p>
        ) : null}
      </div>

      {editingRow && editForm ? (
        <div className="mb-8 rounded-xl border border-amber-200/80 bg-amber-50/40 p-5 shadow-sm dark:border-amber-700/50 dark:bg-navy-800">
          <h5 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
            Edit code
          </h5>
          <form
            onSubmit={handleSaveEdit}
            className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end"
          >
            <label className="flex min-w-[140px] flex-1 flex-col text-sm">
              <span className="mb-1 text-gray-600 dark:text-gray-300">PromoCode</span>
              <input
                value={editForm.promoCode}
                onChange={(e) => setEditForm((f) => ({ ...f, promoCode: e.target.value }))}
                className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
              />
            </label>
            <label className="flex min-w-[100px] flex-col text-sm">
              <span className="mb-1 text-gray-600 dark:text-gray-300">Path</span>
              <select
                value={editForm.path}
                onChange={(e) => setEditForm((f) => ({ ...f, path: e.target.value }))}
                className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
              >
                {PATH_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex min-w-[160px] flex-col text-sm">
              <span className="mb-1 text-gray-600 dark:text-gray-300">Type</span>
              <select
                value={editForm.type}
                onChange={(e) => {
                  const next = e.target.value;
                  setEditForm((f) => ({
                    ...f,
                    type: next,
                    timeLimitMode: next !== "TimeLimited" ? "Limited" : f.timeLimitMode,
                  }));
                }}
                className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            {editForm.type === "TimeLimited" ? (
              <label className="flex min-w-[160px] flex-col text-sm">
                <span className="mb-1 text-gray-600 dark:text-gray-300">Limit</span>
                <select
                  value={editForm.timeLimitMode}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, timeLimitMode: e.target.value }))
                  }
                  className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
                >
                  <option value="Unlimited">Unlimited</option>
                  <option value="Limited">Limited</option>
                </select>
              </label>
            ) : null}
            {editShouldAskNumber ? (
              <label className="flex min-w-[100px] flex-col text-sm">
                <span className="mb-1 text-gray-600 dark:text-gray-300">Number</span>
                <input
                  type="number"
                  min={1}
                  value={editForm.number}
                  onChange={(e) => setEditForm((f) => ({ ...f, number: e.target.value }))}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
                />
              </label>
            ) : null}
            <label className="flex min-w-[100px] flex-col text-sm">
              <span className="mb-1 text-gray-600 dark:text-gray-300">Discount %</span>
              <input
                type="number"
                min={0}
                max={90}
                value={editForm.discountPercent}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, discountPercent: e.target.value }))
                }
                className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
              />
            </label>
            {editForm.type === "TimeLimited" ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <label className="flex flex-col text-sm">
                  <span className="mb-1 text-gray-600 dark:text-gray-300">
                    Period (start)
                  </span>
                  <input
                    type="date"
                    value={editForm.periodStart}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, periodStart: e.target.value }))
                    }
                    className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
                  />
                </label>
                <label className="flex flex-col text-sm">
                  <span className="mb-1 text-gray-600 dark:text-gray-300">
                    Period (end)
                  </span>
                  <input
                    type="date"
                    value={editForm.periodEnd}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, periodEnd: e.target.value }))
                    }
                    className="rounded-lg border border-gray-200 px-3 py-2 text-navy-700 dark:border-white/20 dark:bg-navy-900 dark:text-white"
                  />
                </label>
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={savingEdit}
                className="rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
              >
                {savingEdit ? "…" : "Save"}
              </button>
              <button
                type="button"
                onClick={closeEdit}
                className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-navy-700 hover:bg-gray-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-white/10">
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                PromoCode
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Path
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Used
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Unused
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Total
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Discount %
              </th>
              <th className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {tableLoading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-6 text-gray-500 dark:text-gray-400"
                >
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-6 text-gray-500 dark:text-gray-400"
                >
                  No codes yet. Create the Supabase table if needed:{" "}
                  <code className="text-xs">backend/sql/promo_codes.sql</code>
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-gray-100 dark:border-white/5"
                >
                  <td className="px-4 py-3 font-medium text-navy-700 dark:text-white">
                    {r.promoCode}
                  </td>
                  <td className="px-4 py-3 font-semibold text-navy-700 dark:text-gray-200">
                    {r.path || inferPathFromPromoCode(r.promoCode)}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {r.used}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {r.unused == null ? "Unlimited" : r.unused}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {r.type === "TimeLimited" && r.timeLimitMode === "Unlimited"
                      ? "Unlimited"
                      : r.total}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {formatDiscountPct(r)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(r)}
                        className="rounded-md bg-brand-500/90 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={deletingId === r.id}
                        onClick={() => requestDelete(r)}
                        className="rounded-md border border-red-400/60 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-500/20 dark:text-red-300"
                      >
                        {deletingId === r.id ? "…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {deleteTarget
        ? createPortal(
            <div
              className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 px-4 py-8"
              role="presentation"
              onClick={(e) => {
                if (e.target === e.currentTarget) cancelDelete();
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="promo-delete-title"
                className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-white/15 dark:bg-navy-900"
                onClick={(e) => e.stopPropagation()}
              >
                <h3
                  id="promo-delete-title"
                  className="text-lg font-bold text-navy-800 dark:text-white"
                >
                  Confirm deletion
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                  Permanently delete code{" "}
                  <span className="font-mono font-semibold text-navy-800 dark:text-white">
                    {`"${deleteTarget.promoCode}"`}
                  </span>
                  ? This cannot be undone.
                </p>
                <div className="mt-6 flex flex-wrap justify-end gap-3">
                  <button
                    type="button"
                    disabled={deletingId != null}
                    onClick={cancelDelete}
                    className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-navy-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={deletingId != null}
                    onClick={() => void confirmDelete()}
                    className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {deletingId ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
